/**
 * The shared detail body: tabbed timeline / notes / tasks on the left, a field
 * panel on the right. Every record type uses it; only the side panel differs.
 *
 * This is the Frappe CRM detail layout, rendered in Vireo primitives.
 */

import { useState, type ReactNode } from 'react'
import type { Note, SubjectRef, Task, TimelineResult } from '@erp/contracts'
import { NotesPanel } from './notes-panel'
import { TasksPanel } from './tasks-panel'
import { Table, Tabs } from '../ui'

type TabId = 'activity' | 'notes' | 'tasks'

export function RecordDetail({
  subject,
  timeline,
  notes,
  tasks,
  side,
}: {
  subject: SubjectRef
  timeline: TimelineResult
  notes: readonly Note[]
  tasks: readonly Task[]
  side: ReactNode
}) {
  const [tab, setTab] = useState<TabId>('activity')
  // Deleted rows are loaded so they can be restored, but they must not inflate
  // the tab counts — those describe what is live.
  const liveNotes = notes.filter((note) => note.deletedAt === null).length
  const openTasks = tasks.filter(
    (t) => t.deletedAt === null && t.status !== 'done' && t.status !== 'cancelled',
  ).length

  return (
    <div className="ax-detail">
      <section className="ax-detail-main">
        <Tabs<TabId>
          active={tab}
          onSelect={setTab}
          tabs={[
            { id: 'activity', label: 'Activity', count: timeline.totalCount },
            { id: 'notes', label: 'Notes', count: liveNotes },
            { id: 'tasks', label: 'Tasks', count: openTasks },
          ]}
        />

        {tab === 'activity' ? <Timeline timeline={timeline} /> : null}
        {tab === 'notes' ? <NotesPanel notes={notes} subject={subject} /> : null}
        {tab === 'tasks' ? <TasksPanel tasks={tasks} subject={subject} /> : null}
      </section>

      <aside className="ax-detail-side">{side}</aside>
    </div>
  )
}

export function Timeline({ timeline }: { timeline: TimelineResult }) {
  if (timeline.rows.length === 0) {
    return <p className="ax-text-muted">Nothing has happened yet.</p>
  }

  return (
    <Table>
      <tbody>
        {timeline.rows.map((entry) => (
          <tr key={entry.id}>
            <td className="ax-timeline-kind">{entry.kind.replace('_', ' ')}</td>
            <td>{describe(entry.payload)}</td>
            <td className="ax-timeline-when">
              {new Intl.DateTimeFormat(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(new Date(entry.occurredAt))}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

/** The discriminated payload union pays off here — each branch is fully typed. */
function describe(payload: TimelineResult['rows'][number]['payload']): string {
  switch (payload.kind) {
    case 'note':
      return payload.title !== null ? `Note added: ${payload.title}` : 'Note added'
    case 'note_deleted':
      return payload.title !== null ? `Note deleted: ${payload.title}` : 'Note deleted'
    case 'note_restored':
      return payload.title !== null ? `Note restored: ${payload.title}` : 'Note restored'
    case 'email':
      return `${payload.direction === 'inbound' ? 'Received' : 'Sent'}: ${payload.subject}`
    case 'call':
      return `${payload.direction === 'inbound' ? 'Inbound' : 'Outbound'} call${
        payload.durationSeconds !== null ? ` · ${Math.round(payload.durationSeconds / 60)}m` : ''
      }`
    case 'meeting':
      return payload.title
    case 'task':
      return `Task added: ${payload.title}`
    case 'task_deleted':
      return `Task deleted: ${payload.title}`
    case 'task_restored':
      return `Task restored: ${payload.title}`
    case 'status_change':
      return 'Status changed'
    case 'assignment':
      return payload.action === 'assigned' ? 'Assigned' : 'Unassigned'
    case 'field_change':
      return `${payload.field} changed`
  }
}

/** Read-only field row for the side panel. */
export function Detail({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div className="ax-field">
      <span className="ax-field-label">{label}</span>
      <span className="ax-value">{value === null || value === '' ? '—' : value}</span>
    </div>
  )
}
